import type { Home } from '@lmc/ts-common'
import { Avatar, EmptyState, NoDataIcon, Spinner, theme } from '@lmc/ui-kit'
import { Heading, Text } from '@lmc/ui-kit/src/general'
import { Drawer, Pagination, Tag, Tooltip } from 'antd'
import { getNominative } from 'common/utils'
import type { Dayjs } from 'dayjs'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { formatDisplayName } from 'screens/Rostering/Reconciliation/reconciliationUtils'
import DeactivatedIcon from 'screens/Rostering/RotaBuilder/common/components/icons/DeactivatedIcon'
import DeletedIcon from 'screens/Rostering/RotaBuilder/common/components/icons/DeletedIcon'
import styled from 'styled-components'
import { usePayReportDrawer } from '../hooks/usePayReportDrawer'
import type { PayReportRow } from '../types/payReport'
import { downloadPayReportCsv } from './downloadPayReportCsv'
import { PayReportDrawerFooterBar } from './PayReportBreakdownFooter'
import { PayReportBreakdownTable } from './PayReportBreakdownTable'
import { mapDrawerApiRowsToTable } from './payReportDrawerMapper'
import {
  PAY_REPORT_DELETED_TM_SUPPORT_URL,
  resolvePayReportTeamMemberStatus
} from './payReportTeamMemberStatus'

type PayReportBreakdownDrawerProps = {
  open: boolean
  onClose: () => void
  dateRange: [Dayjs, Dayjs]
  row: PayReportRow | null
}

const DRAWER_PAGE_SIZE = 5

export const PayReportBreakdownDrawer = ({
  open,
  onClose,
  dateRange,
  row
}: PayReportBreakdownDrawerProps) => {
  const { t } = useTranslation()

  const preferredPeopleNominative = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredPeopleNominative
  )
  const nominative = getNominative(preferredPeopleNominative, 'Resident')

  const [drawerPage, setDrawerPage] = useState(1)
  const [csvLoading, setCsvLoading] = useState(false)

  const drawerParams = row
    ? {
        teamMemberId: row.teamMemberId,
        startTime: dateRange[0].startOf('day').toISOString(),
        endTime: dateRange[1].endOf('day').toISOString(),
        page: drawerPage,
        limit: DRAWER_PAGE_SIZE
      }
    : null

  const { data, isLoading, isError, isFetching } = usePayReportDrawer(
    drawerParams,
    open && Boolean(row)
  )

  const handleDownloadCsv = useCallback(async () => {
    if (!row) {
      return
    }
    setCsvLoading(true)
    try {
      await downloadPayReportCsv({
        startTime: dateRange[0].startOf('day').toISOString(),
        endTime: dateRange[1].endOf('day').toISOString(),
        teamMemberIds: [row.teamMemberId]
      })
    } finally {
      setCsvLoading(false)
    }
  }, [dateRange, row])

  const tableRows = useMemo(
    () => (data?.rows ? mapDrawerApiRowsToTable(data.rows) : []),
    [data?.rows]
  )
  const hasTableRows = tableRows.length > 0

  const summary = data?.summary
  const pagination = data?.pagination

  const rangeLabel = `${dateRange[0].format('DD-MM-YYYY')} - ${dateRange[1].format('DD-MM-YYYY')}`

  const teamName = row ? formatDisplayName(row.teamMember.name) : ''

  const drawerMemberStatus = row
    ? resolvePayReportTeamMemberStatus(row)
    : 'active'

  return (
    <StyledDrawer
      title={
        row ? (
          <HeaderBlock>
            <HeaderTopRow>
              <RangeMuted level={2}>{rangeLabel}</RangeMuted>
              {row.payRateCard?.trim() || row.payRateDisplay?.trim() ? (
                <Tag>
                  {row.payRateCard?.trim() || row.payRateDisplay?.trim()}
                </Tag>
              ) : null}
            </HeaderTopRow>
            <ProfileRow>
              <Avatar url={row.teamMember.picture} size={48} />
              <Heading level={4}>{teamName}</Heading>
              {drawerMemberStatus === 'deleted' ? (
                <Tooltip
                  color={theme.color.neutral.color3}
                  placement="bottom"
                  title={
                    <TooltipInner>
                      {t('TM_DELETED_TOOLTIP')}{' '}
                      <TooltipLink
                        href={PAY_REPORT_DELETED_TM_SUPPORT_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t('HERE')}
                      </TooltipLink>
                      .
                    </TooltipInner>
                  }
                >
                  <StatusIconWrap>
                    <DeletedIcon />
                  </StatusIconWrap>
                </Tooltip>
              ) : drawerMemberStatus === 'inactive' ? (
                <Tooltip
                  color={theme.color.neutral.color3}
                  placement="bottom"
                  title={
                    <TooltipInner>{t('TM_DEACTIVATED_TOOLTIP')}</TooltipInner>
                  }
                >
                  <StatusIconWrap>
                    <DeactivatedIcon />
                  </StatusIconWrap>
                </Tooltip>
              ) : null}
            </ProfileRow>
          </HeaderBlock>
        ) : null
      }
      placement="right"
      width={1400}
      onClose={onClose}
      open={open && Boolean(row)}
      destroyOnClose
      footer={
        summary?.eventTotals ? (
          <PayReportDrawerFooterBar
            eventTotals={summary.eventTotals}
            t={t}
            onDownloadCsv={handleDownloadCsv}
            csvLoading={csvLoading}
          />
        ) : null
      }
    >
      {row ? (
        <DrawerInner>
          {isLoading ? (
            <LoadingBlock>
              <Spinner />
            </LoadingBlock>
          ) : isError ? (
            <ErrorText>{t('PAY_REPORT_BREAKDOWN_LOAD_ERROR')}</ErrorText>
          ) : (
            <>
              <SummaryBar>
                <SummaryItem>
                  <SummaryLabel level={2}>
                    {t('PAY_REPORT_DRAWER_STATS_TOTAL_RECONCILED_PAY')}
                  </SummaryLabel>
                  <SummaryValue>
                    {summary?.careDeliveryPay ?? '-'}{' '}
                    {t('RECONCILIATION_GBP_SUFFIX')}
                  </SummaryValue>
                </SummaryItem>
                <SummaryItem>
                  <SummaryLabel level={2}>
                    {t('PAY_REPORT_DRAWER_STATS_RECONCILED_HOURS')}
                  </SummaryLabel>
                  <SummaryValue>
                    {summary?.confirmedHours ?? '-'}{' '}
                    {t('PAY_REPORT_BREAKDOWN_HOURS_UNIT')}
                  </SummaryValue>
                </SummaryItem>
                <SummaryItem>
                  <SummaryLabel level={2}>
                    {t('PAY_REPORT_DRAWER_STATS_TOTAL_TRAVEL_PAY')}
                  </SummaryLabel>
                  <SummaryValue>
                    {summary?.totalTravelPay ?? '-'}{' '}
                    {t('RECONCILIATION_GBP_SUFFIX')}
                  </SummaryValue>
                </SummaryItem>
                <SummaryItem>
                  <SummaryLabel level={2}>
                    {t('PAY_REPORT_DRAWER_STATS_TOTAL_PAY')}
                  </SummaryLabel>
                  <SummaryValue>
                    {summary?.totalPay ?? '-'} {t('RECONCILIATION_GBP_SUFFIX')}
                  </SummaryValue>
                </SummaryItem>
              </SummaryBar>

              <TableWrap>
                {hasTableRows ? (
                  <PayReportBreakdownTable
                    rows={tableRows}
                    loading={isLoading || isFetching}
                    nominative={nominative}
                  />
                ) : (
                  <EmptyStateWrap>
                    <EmptyState
                      icon={<NoDataIcon />}
                      title={t('PAY_REPORT_BREAKDOWN_EMPTY_TITLE')}
                      description={t('PAY_REPORT_BREAKDOWN_EMPTY_DESCRIPTION')}
                    />
                  </EmptyStateWrap>
                )}
              </TableWrap>

              {pagination && pagination.totalItems > 0 ? (
                <PaginationWrap>
                  <Pagination
                    current={pagination.currentPage}
                    total={pagination.totalItems}
                    pageSize={pagination.itemsPerPage}
                    onChange={(p) => setDrawerPage(p)}
                    showSizeChanger={false}
                  />
                </PaginationWrap>
              ) : null}
            </>
          )}
        </DrawerInner>
      ) : null}
    </StyledDrawer>
  )
}

const StyledDrawer = styled(Drawer)`
  .ant-drawer-header {
    padding: 20px 24px;
    border-bottom: 1px solid ${({ theme }) => theme.color.neutral.color8};
    box-shadow: none;
  }

  .ant-drawer-header-title {
    flex-direction: row-reverse;
    align-items: flex-start;
  }

  .ant-drawer-close {
    margin: 0;
    padding: 4px;
  }

  .ant-drawer-footer {
    padding: 24px;
    box-shadow: 0px -2px 4px 0px rgba(0, 0, 0, 0.1);
  }
`

const DrawerInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const HeaderBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-right: 8px;
`

const HeaderTopRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
`

const RangeMuted = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color5};
`

const ProfileRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`

const StatusIconWrap = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  width: 14px;
`

const TooltipInner = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color11};
`

const TooltipLink = styled.a`
  text-decoration: underline;
  color: ${({ theme }) => theme.color.status.primary};
`

const SummaryBar = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 20px 24px;
  padding: 20px 24px;
  border-radius: 16px;
  background: ${({ theme }) => theme.color.neutral.color10};

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`

const SummaryItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`

const SummaryLabel = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color5};
`

const SummaryValue = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
`

const TableWrap = styled.div`
  width: 100%;
  min-width: 0;
  border-radius: 20px;
  overflow: hidden;
`

const EmptyStateWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 320px;
  width: 100%;
  border-radius: 20px;
  background: ${({ theme }) => theme.color.neutral.color10};
`

const PaginationWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: 8px 0 0;
`

const LoadingBlock = styled.div`
  display: flex;
  justify-content: center;
  padding: 48px 0;
`

const ErrorText = styled(Text)`
  color: #f5222d;
`
